import type { CaseStatus } from '@contract';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { z } from 'zod';
import { CASE_LIST_COLUMNS } from '@/components/case/case-columns';
import { DataTable } from '@/components/data-table';
import { ExportButton } from '@/components/export-button';
import { EmptyState } from '@/components/feedback/empty-state';
import { QueryState } from '@/components/feedback/query-state';
import { SkeletonTable } from '@/components/feedback/skeletons';
import { PageHeader } from '@/components/page-header';
import { useSheetActions } from '@/components/sheet/use-sheet';
import { TabLinks } from '@/components/tab-links';
import { adminQuery } from '@/lib/query';

const VIEWS = [
  { key: 'open', label: 'Open' },
  { key: 'waiting_customer', label: 'Waiting on customer' },
  { key: 'waiting_provider', label: 'Waiting on provider' },
  { key: 'mine', label: 'Mine' },
  { key: 'closed', label: 'Closed' },
  { key: 'all', label: 'All' },
] as const;

type View = (typeof VIEWS)[number]['key'];

const searchSchema = z.object({
  view: z
    .enum([
      'open',
      'waiting_customer',
      'waiting_provider',
      'mine',
      'closed',
      'all',
    ])
    .catch('open')
    .default('open'),
});

/** Dispute cases by where they are waiting, the ones I own, the closed ones. */
export const Route = createFileRoute('/_app/cases/')({
  validateSearch: searchSchema,
  component: CasesPage,
});

/** What each view asks the server for. "Mine" is every case of mine that is not closed. */
function argsFor(view: View): {
  status?: CaseStatus | 'active';
  owner?: string;
} {
  switch (view) {
    case 'mine':
      return { status: 'active', owner: 'me' };
    case 'all':
      return {};
    default:
      return { status: view };
  }
}

const EMPTY: Record<View, string> = {
  open: 'No open cases.',
  waiting_customer: 'No case is waiting on a customer.',
  waiting_provider: 'No case is waiting on a provider.',
  mine: 'You own no open cases.',
  closed: 'No closed cases.',
  all: 'No cases yet.',
};

function CasesPage() {
  const { view } = Route.useSearch();
  const args = argsFor(view);
  const list = useQuery(adminQuery('cases.list', args));
  const sheetActions = useSheetActions();

  return (
    <>
      <PageHeader
        title="Cases"
        description="Disputes about money. A case groups one customer's payments, reports and claims."
        actions={<ExportButton source="cases.list" args={args} />}
      />
      <TabLinks
        tabs={VIEWS}
        active={view}
        render={(tab, domProps) => (
          <Link {...domProps} to="/cases" search={{ view: tab.key as View }} />
        )}
      />
      <QueryState
        query={list}
        label="cases"
        skeleton={<SkeletonTable columns={9} />}
      >
        {(data) =>
          data.rows.length === 0 ? (
            <EmptyState variant="clear" title={EMPTY[view]} />
          ) : (
            <DataTable
              id="cases"
              columns={CASE_LIST_COLUMNS}
              data={data.rows}
              rowLabel={(row) => `Open case ${row.id.slice(0, 8)}`}
              onRowClick={(row, rowEl) =>
                sheetActions.open(
                  { type: 'case', id: row.id },
                  { fromRowEl: rowEl.querySelector('button') ?? rowEl },
                )
              }
            />
          )
        }
      </QueryState>
    </>
  );
}
